// ==================== BGV Enums ====================

export enum BgvCaseStatus {
  Pending = 0,
  InProgress = 1,
  Completed = 2,
  Failed = 3,
  SubmittedForHrReview = 4,
}

export const BgvCaseStatusLabels: Record<BgvCaseStatus, string> = {
  [BgvCaseStatus.Pending]: 'Pending',
  [BgvCaseStatus.InProgress]: 'In Progress',
  [BgvCaseStatus.Completed]: 'Completed',
  [BgvCaseStatus.Failed]: 'Failed',
  [BgvCaseStatus.SubmittedForHrReview]: 'Submitted for HR Review',
};

export const BgvCaseStatusColors: Record<BgvCaseStatus, 'default' | 'warning' | 'info' | 'success' | 'error'> = {
  [BgvCaseStatus.Pending]: 'warning',
  [BgvCaseStatus.InProgress]: 'info',
  [BgvCaseStatus.Completed]: 'success',
  [BgvCaseStatus.Failed]: 'error',
  [BgvCaseStatus.SubmittedForHrReview]: 'info',
};

export enum BgvTaskStatus {
  NotStarted = 0,
  InProgress = 1,
  Verified = 2,
  DiscrepancyFound = 3,
  Rejected = 4,
}

export const BgvTaskStatusLabels: Record<BgvTaskStatus, string> = {
  [BgvTaskStatus.NotStarted]: 'Not Started',
  [BgvTaskStatus.InProgress]: 'In Progress',
  [BgvTaskStatus.Verified]: 'Verified',
  [BgvTaskStatus.DiscrepancyFound]: 'Discrepancy Found',
  [BgvTaskStatus.Rejected]: 'Rejected',
};

export const BgvTaskStatusColors: Record<BgvTaskStatus, 'default' | 'warning' | 'info' | 'success' | 'error'> = {
  [BgvTaskStatus.NotStarted]: 'default',
  [BgvTaskStatus.InProgress]: 'info',
  [BgvTaskStatus.Verified]: 'success',
  [BgvTaskStatus.DiscrepancyFound]: 'warning',
  [BgvTaskStatus.Rejected]: 'error',
};

export enum BgvDocumentStatus {
  Uploaded = 0,
  Approved = 1,
  Rejected = 2,
}

export const BgvDocumentStatusLabels: Record<BgvDocumentStatus, string> = {
  [BgvDocumentStatus.Uploaded]: 'Uploaded',
  [BgvDocumentStatus.Approved]: 'Approved',
  [BgvDocumentStatus.Rejected]: 'Rejected',
};

export enum BgvDiscrepancySeverity {
  Minor = 0,
  Major = 1,
  Critical = 2,
}

export const BgvDiscrepancySeverityLabels: Record<BgvDiscrepancySeverity, string> = {
  [BgvDiscrepancySeverity.Minor]: 'Minor',
  [BgvDiscrepancySeverity.Major]: 'Major',
  [BgvDiscrepancySeverity.Critical]: 'Critical',
};

export const BgvDiscrepancySeverityColors: Record<BgvDiscrepancySeverity, 'default' | 'warning' | 'error'> = {
  [BgvDiscrepancySeverity.Minor]: 'default',
  [BgvDiscrepancySeverity.Major]: 'warning',
  [BgvDiscrepancySeverity.Critical]: 'error',
};

export enum BgvDiscrepancyStatus {
  Open = 0,
  UnderReview = 1,
  Resolved = 2,
  Escalated = 3,
}

export const BgvDiscrepancyStatusLabels: Record<BgvDiscrepancyStatus, string> = {
  [BgvDiscrepancyStatus.Open]: 'Open',
  [BgvDiscrepancyStatus.UnderReview]: 'Under Review',
  [BgvDiscrepancyStatus.Resolved]: 'Resolved',
  [BgvDiscrepancyStatus.Escalated]: 'Escalated',
};

export enum BgvFinalDecision {
  Pending = 0,
  Clear = 1,
  Concern = 2,
  NotClear = 3,
}

export const BgvFinalDecisionLabels: Record<BgvFinalDecision, string> = {
  [BgvFinalDecision.Pending]: 'Pending',
  [BgvFinalDecision.Clear]: 'Clear',
  [BgvFinalDecision.Concern]: 'Concern',
  [BgvFinalDecision.NotClear]: 'Not Clear',
};

export const BgvFinalDecisionColors: Record<BgvFinalDecision, 'default' | 'warning' | 'success' | 'error'> = {
  [BgvFinalDecision.Pending]: 'default',
  [BgvFinalDecision.Clear]: 'success',
  [BgvFinalDecision.Concern]: 'warning',
  [BgvFinalDecision.NotClear]: 'error',
};

// ==================== BGV DTOs ====================

export interface BgvCaseDto {
  id: string;
  employeeId: string;
  employeeName: string;
  employeeCode: string;
  caseNumber: string;
  status: BgvCaseStatus;
  finalDecision: BgvFinalDecision;
  assignedToId: string | null;
  assignedToName: string | null;
  vendorId: string | null;
  vendorName: string | null;
  remarks: string | null;
  createdAt: string;
  completedAt: string | null;
  totalTasks: number;
  completedTasks: number;
}

export interface BgvCaseDetailDto extends BgvCaseDto {
  tasks: BgvTaskDto[];
  discrepancies: BgvDiscrepancyDto[];
  activityLogs: BgvActivityLogDto[];
}

export interface BgvTaskDto {
  id: string;
  caseId: string;
  verificationTypeId: string;
  verificationTypeName: string;
  status: BgvTaskStatus;
  assignedToId: string | null;
  assignedToName: string | null;
  vendorId: string | null;
  vendorName: string | null;
  remarks: string | null;
  createdAt: string;
  completedAt: string | null;
  documents: BgvDocumentDto[];
}

export interface BgvDocumentDto {
  id: string;
  taskId: string;
  fileName: string;
  filePath: string;
  contentType: string | null;
  status: BgvDocumentStatus;
  reviewRemarks: string | null;
  uploadedAt: string;
}

export interface BgvDiscrepancyDto {
  id: string;
  caseId: string;
  taskId: string | null;
  taskName: string | null;
  description: string;
  severity: BgvDiscrepancySeverity;
  status: BgvDiscrepancyStatus;
  hrRemarks: string | null;
  candidateClarification: string | null;
  clarificationFilePath: string | null;
  createdAt: string;
}

export interface BgvVendorDto {
  id: string;
  name: string;
  contactPerson: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  isActive: boolean;
  activeCases: number;
  hasLoginCredentials: boolean;
  loginEmail: string | null;
  username: string | null;
}

export interface BgvVerificationTypeDto {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
}

export interface BgvActivityLogDto {
  id: string;
  caseId: string;
  action: string;
  details: string | null;
  performedBy: string;
  createdAt: string;
}

export interface BgvDashboardDto {
  totalCases: number;
  pendingCases: number;
  inProgressCases: number;
  completedCases: number;
  failedCases: number;
  clearCases: number;
  concernCases: number;
  notClearCases: number;
  averageTatDays: number;
  vendorPerformance: BgvVendorPerformanceDto[];
}

export interface BgvVendorPerformanceDto {
  vendorId: string;
  vendorName: string;
  totalTasks: number;
  completedTasks: number;
  averageTatDays: number;
}

// ==================== Create/Update DTOs ====================

export interface CreateBgvCase {
  employeeId: string;
  assignedToId?: string;
  vendorId?: string;
  remarks?: string;
  verificationTypeIds: string[];
}

export interface CreateBgvVendor {
  name: string;
  contactPerson?: string;
  email?: string;
  phone?: string;
  address?: string;
}

export interface UpdateBgvVendor extends CreateBgvVendor {
  id: string;
  isActive: boolean;
}

export interface CreateBgvVerificationType {
  name: string;
  description?: string;
}

export interface CreateBgvDiscrepancy {
  caseId: string;
  taskId?: string;
  description: string;
  severity: BgvDiscrepancySeverity;
  hrRemarks?: string;
}
